/**
 * threadListView.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import Picker from "@emoji-mart/react";
import * as React from "react";

import MenuIcon from "@mui/icons-material/Menu";
import { ClickAwayListener, IconButton, Popper } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { GroupNavContext } from "../chat/groupNav.context";

import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { ThreadMessageListView } from "./threadMessageListView.component"; 

import { PermissionLevel } from "../models/auth";
import { CustomEmoji, EmojiSelection, emojiSelectionToContent } from "../models/reaction";

import { groupServiceFactory, MessageThread } from "../services/group.service";
import { workerServiceFactory } from "../services/worker.service";
import { ContentMessage } from "../models/message";
import { dbServiceFactory } from "../services/db.service";

interface ThreadWithRef extends MessageThread {
  ref?: React.RefObject<HTMLDivElement | null>
}

interface Props {
  groupNavContext: GroupNavContext
}
interface State {
  threads: Array<MessageThread>,
  openEmojiPicker: boolean,
  anchorEl: HTMLElement | null,
  reactingMessage: ContentMessage | null,
  reactingThread: MessageThread | null,
  errMsg: string
}
// This is now a class component because of the need to subscribe to worker events
export class ThreadListViewComponent extends React.Component<Props, State> {
  static contextType = AuthContext;
  declare context: React.ContextType<typeof AuthContext>;

  constructor(props: Props) {
    super(props);
    this.state = {
      threads: [],
      openEmojiPicker: false,
      anchorEl: null,
      reactingMessage: null,
      reactingThread: null,
      errMsg: ""
    }
    this.onNewMessage = this.onNewMessage.bind(this);
    this.onRemoveMessage = this.onRemoveMessage.bind(this);
    this.onReaction = this.onReaction.bind(this);
    this.onRemoveReaction = this.onRemoveReaction.bind(this);
    this.showEmojiPicker = this.showEmojiPicker.bind(this);
    this.choseReaction = this.choseReaction.bind(this);
  }

  componentDidMount() {
    const context: AuthContext = this.context;
    const workerService = workerServiceFactory();
    workerService.on("message", this.onNewMessage);
    workerService.on("delmsg", this.onRemoveMessage);
    workerService.on("reaction", this.onReaction);
    workerService.on("remReaction", this.onRemoveReaction);

    if (!context.membership) { return; }
    const groupService = groupServiceFactory();

    groupService.getParticipatingThreadsForAccount(context.membership.accountID)
    .then(threads => {
      this.setState({threads: threads});
    }, err => {
      console.error("Error getting threads", err);
      this.setState({errMsg: (err as Error).message});
    });
  }
  componentWillUnmount() {
    const workerService = workerServiceFactory();
    workerService.off("message", this.onNewMessage);
    workerService.off("delmsg", this.onRemoveMessage);
    workerService.off("reaction", this.onReaction);
    workerService.off("remReaction", this.onRemoveReaction);
  }

  async onNewMessage (msgID: string, parent: string | undefined) {
    if (!parent) { return; }
    const groupService = groupServiceFactory();
    const msg = await groupService.getReply(msgID);
    if (!msg || !msg._id) { return; }

    let notfound = true;
    const newThreads = Array.from(this.state.threads);
    for (const thread of newThreads) {
      if (thread.main._id === parent) {
        thread.replies.push(msg);
        notfound = false;
        break;
      }
    }
    if (notfound) {
      const newThread = await groupService.getThread(parent);
      newThreads.unshift(newThread);
    }
    this.setState({threads: newThreads});
  }

  async onRemoveMessage (groupID: string, msgID: string | undefined) {
    if (!msgID) { return; }

    for (const thread of this.state.threads) {
      if (thread.main.groupID !== groupID) { continue; }

      if (thread.main._id === msgID) {
        const newThreads = this.state.threads.filter(t => t.main._id !== msgID);
        this.setState({threads: newThreads});
        return;
      }
      const index = thread.replies.findIndex(r => r._id === msgID);
      if (index >= 0) {
        thread.replies.splice(index, 1);
        if (thread.replies.length <= 0) {
          const newThreads = this.state.threads.filter(t => t.main._id !== thread.main._id);
          this.setState({threads: newThreads});
        } else {
        this.setState({threads: Array.from(this.state.threads)});
        }
        return;
      }
    }
  }

  async onReaction(msgID: string, reactionID: string, userID: string,
      contentID: string) {

    let message: ContentMessage | null = null;
    for (const thread of this.state.threads) {
      if (thread.main._id === msgID) { message = thread.main as ContentMessage; break; }

      for (const reply of thread.replies) {
        if (reply._id === msgID) { message = reply as ContentMessage; break; }
      }
      if (message) { break; }
    }
    if (!message) { return; }

    try {
      const reaction = await dbServiceFactory().getReaction(reactionID);
      if (!reaction) { return; }

      const reactionUser = {
        reactionID: reactionID,
        userID: userID,
        firstname: reaction.firstname,
        lastname: reaction.lastname
      };

      if (!message.reactions) { message.reactions = {}; }
      if (!message.reactions[contentID]) {
        if (!reaction.users) { reaction.users = []; }
        reaction.users.push(reactionUser);
        message.reactions[contentID] = reaction;
      } else {
        const reaction = message.reactions[contentID];
        if (!reaction.users) { reaction.users = []; }
        reaction.users.push(reactionUser);
      }

      this.setState({threads: Array.from(this.state.threads)});
    } catch (err) {
      console.error("Error adding reaction:", err);
      this.setState({errMsg: (err as Error).message});
    }
  }

  async onRemoveReaction(msgID: string, contentID: string, userID: string) {
    let message: ContentMessage | null = null;
    for (const thread of this.state.threads) {
      if (thread.main._id === msgID) { message = thread.main as ContentMessage; break; }

      for (const reply of thread.replies) {
        if (reply._id === msgID) { message = reply as ContentMessage; break; }
      }
      if (message) { break; }
    }
    if (!message) { return; }

    if (!message.reactions || !message.reactions[contentID]) { return; }
    const reaction = message.reactions[contentID];
    if (!reaction.users) { return; }

    const index = reaction.users.findIndex(u => u.userID === userID);
    if (index < 0) { return; }

    reaction.users.splice(index, 1);
    if (reaction.users.length <= 0) {
      delete message.reactions[contentID];
    }

    this.setState({threads: Array.from(this.state.threads)});
  }

  showEmojiPicker (anchor: HTMLElement, msg: ContentMessage, thread: MessageThread) {
    this.setState({
      reactingMessage: msg,
      reactingThread: thread,
      anchorEl: anchor,
      openEmojiPicker: true
    });
  }

  async choseReaction (emoji: EmojiSelection) {
    const reactingMessage = this.state.reactingMessage;
    if (!reactingMessage || !this.context.membership) { return; }

    if (!reactingMessage.reactions) {
      reactingMessage.reactions = {};
    }
    const reactions = reactingMessage.reactions;
    const existing = reactions[emoji.id];

    if (existing && existing.users &&
        existing.users.findIndex(u => u.userID === this.context.membership?.userID) >= 0) {
      this.setState({
        openEmojiPicker: false,
        reactingMessage: null,
        reactingThread: null,
        anchorEl: null
      });
      return;
    }

    const { content, contentType } = emojiSelectionToContent(emoji);
    const {accountID, firstname, lastname} = this.context.membership;
    
    const reactingThread = this.state.reactingThread;
    try {
      const tID = reactingMessage._id === reactingThread?.main._id ? "" :
        reactingMessage.threadID;
      await groupServiceFactory().sendReaction(accountID,
        reactingMessage.groupID, reactingMessage._id, tID,
        emoji.id, contentType, content,
        firstname, lastname);

      this.setState({threads: Array.from(this.state.threads)});
    } catch (err) {
      console.error("Error adding reaction:", err);
      this.setState({errMsg: (err as Error).message});
    }
    this.setState({
      openEmojiPicker: false,
      reactingMessage: null,
      reactingThread: null,
      anchorEl: null
    });
  }

  render() {
    return <>
      <header className="group-header-nav attached-left-nav">
        <IconButton className="left-nav-outer-toggle" size="small"
            disableRipple disableTouchRipple
            onClick={this.props.groupNavContext.toggleGroupList}>
          <MenuIcon />
        </IconButton>
        <h3>Threads: {this.context.membership?.accountname || ""}</h3>
        <span className="right-controls">
        </span>
      </header>
      <div className="main-section-inner-with-header thread-list-container">
        {this.state.threads.map(thread => <ThreadViewComponent key={thread.main._id}
          thread={thread}
          reactingMessage={this.state.reactingMessage}
          showEmojiPicker={this.showEmojiPicker}
          onError={msg => this.setState({errMsg: msg})}/>)}
      </div>
      <Popper open={this.state.openEmojiPicker} anchorEl={this.state.anchorEl}
          disablePortal placement="auto-end">
        <ClickAwayListener onClickAway={() => { this.setState({openEmojiPicker: false, anchorEl: null}); }}>
          <div>
            <Picker custom={CustomEmoji} onEmojiSelect={this.choseReaction} />
          </div>
        </ClickAwayListener>
      </Popper>
      <ErrorSnackBar errMsg={this.state.errMsg} didClose={() => this.setState({errMsg: ""})} />
    </>;
  }
}

interface ThreadViewProps {
  thread: ThreadWithRef,
  reactingMessage?: ContentMessage | null,
  showEmojiPicker(anchor: HTMLElement, msg: ContentMessage, thread: MessageThread): void,
  onError: (msg: string) => void
}

function ThreadViewComponent(props: ThreadViewProps): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const groupNavContext = React.useContext<GroupNavContext>(GroupNavContext);
  const [canSendMessages, setCanSendMessages] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const canWrite = groupNavContext.can(PermissionLevel.WRITE, "messages",
      props.thread.main.groupID);
    setCanSendMessages(canWrite);

  }, [authContext.membership, groupNavContext.groupMembershipMap]);

  props.thread.ref = ref;

  const groupMembership = groupNavContext.groupMembershipMap[props.thread.main.groupID];
  if (!groupMembership) { return <></>; }

  async function doMarkThreadAsRead() {
    if (!authContext.membership) { return; }
    try {
      const accountID = authContext.membership?.accountID;
      const userID = authContext.membership?.userID;
      await groupServiceFactory().markThreadAsRead(accountID, userID,
        props.thread.main.groupID, props.thread.main._id);
    } catch (err) {
      console.error("Error marking thread as read:", err);
      props.onError((err as Error).message);
    }
  }

  return <div ref={ref} className="thread-list-inner-container">
    <div>
      <h4 className="thread-header">{groupMembership.groupname}</h4>
    </div>
    <ThreadMessageListView
        threadID={props.thread.main._id}
        canSendMessages={canSendMessages}
        startTruncated={true}
        mainMessage={props.thread.main}
        reactingMessage={props.reactingMessage}
        replies={props.thread.replies}
        getMatchingMembers={groupNavContext.getMatchingMembers}
        showEmojiPicker={(a, m) => props.showEmojiPicker(a, m, props.thread)}
        onFocus={doMarkThreadAsRead}
        didExpandList={doMarkThreadAsRead}
        onError={props.onError}/>
  </div>;
}